import { Link } from 'react-router-dom';
import { useState, useCallback, useEffect } from 'react';
import { roofingData } from '../../mocks/roofingData';
import WakeCountyChart from './components/WakeCountyChart';

function pickThreeRandom(len: number): number[] {
  const pool = [...Array(len).keys()];
  const out: number[] = [];
  while (out.length < 3) {
    const r = Math.floor(Math.random() * pool.length);
    out.push(pool[r]);
    pool.splice(r, 1);
  }
  return out;
}

export default function SimulationPage() {
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';

    // --- WebPage + Dataset Schema ---
    const existing = document.getElementById('simulation-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'simulation-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Wake County Roofing Market Data – Price vs. Quality Analysis',
      description:
        'Interactive Wake County roofing market data: explore price, quality, and value scores for 100+ local contractors. See why getting 2-3 random bids is not enough. Serving Raleigh, Cary & Apex NC.',
      url: `${siteUrl}/wake-county-simulation`,
      isPartOf: { '@type': 'WebSite', name: 'OverUnder Roofing', url: siteUrl },
      about: {
        '@type': 'Dataset',
        name: 'Wake County Roofing Contractor Market Data',
        description:
          'Price, quality, and value scores for 100 roofing contractors in Wake County, North Carolina. Includes Raleigh, Cary, Apex, Wake Forest, Holly Springs, Fuquay-Varina, and Garner.',
        spatialCoverage: {
          '@type': 'Place',
          name: 'Wake County, North Carolina, USA',
        },
        variableMeasured: [
          { '@type': 'PropertyValue', name: 'Quality Score', unitText: 'points out of 10' },
          { '@type': 'PropertyValue', name: 'Cost', unitCode: 'USD' },
          { '@type': 'PropertyValue', name: 'Value Score', unitText: 'ratio' },
        ],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Market Data', item: `${siteUrl}/wake-county-simulation` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('simulation-schema');
      if (el) el.remove();
    };
  }, []);

  const handleSimulate = useCallback(() => {
    setIsAnimating(true);
    setSelectedIndices([]);
    setTimeout(() => {
      setSelectedIndices(pickThreeRandom(roofingData.length));
      setIsAnimating(false);
    }, 700);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* React 19 document metadata */}
      <title>Wake County Roofing Market Data – Price vs. Quality Analysis | OverUnder Roofing</title>
      <meta name="description" content="Interactive Wake County roofing market data: explore price, quality, and value scores for 100+ local contractors. See why getting 2-3 random bids isn't enough. Serving Raleigh, Cary & Apex NC." />
      <link rel="canonical" href="https://overunderroofing.com/wake-county-simulation" />

      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <Link to="/" className="flex items-center gap-2 cursor-pointer group">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
            <i className="ri-home-4-line text-white text-sm" />
          </div>
          <span className="font-bold text-slate-800 text-sm group-hover:text-emerald-700 transition-colors">
            OverUnder Roofing
          </span>
        </Link>
        <Link
          to="/"
          className="text-sm text-slate-500 hover:text-emerald-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          Back to Home
        </Link>
      </nav>

      <main>
        {/* ─── Full Market Data Section ─── */}
        <section
          id="market-data"
          className="bg-slate-50/50"
        >
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Section header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-4 py-1.5 mb-4">
                <i className="ri-database-2-line text-red-500 text-sm" />
                <span className="text-xs font-semibold text-red-600">Wake County Market Data</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2">
                Full Wake County Roofing Market
              </h1>
              <p className="text-slate-500 text-sm max-w-xl mx-auto">
                100 companies analyzed. Hover each dot to see quality, cost, and value scores. Most homeowners never see this picture.
              </p>

              {/* Stat pills */}
              <div className="flex flex-wrap justify-center gap-3 mt-5">
                <div className="bg-white border border-slate-200 rounded-full px-5 py-2 text-sm">
                  <span className="font-bold text-slate-800">100</span>
                  <span className="text-slate-500 ml-1">companies analyzed</span>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-full px-5 py-2 text-sm">
                  <span className="font-bold text-emerald-700">
                    {roofingData.filter(d => d.value > 0.88 && d.quality > 8.6).length}
                  </span>
                  <span className="text-emerald-600 ml-1">sweet spot companies</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-full px-5 py-2 text-sm">
                  <span className="font-bold text-amber-700">
                    {Math.round((roofingData.filter(d => d.value > 0.88 && d.quality > 8.6).length / roofingData.length) * 100)}%
                  </span>
                  <span className="text-amber-600 ml-1">chance you find one randomly</span>
                </div>
              </div>
            </div>

            {/* Chart + Simulation Panel */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="border-b border-slate-100 px-6 py-4">
                <h3 className="font-bold text-slate-800 text-base">
                  Price vs. Quality vs. Value — 100 Wake County Roofers
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Higher value score = better combination of quality and price
                </p>
              </div>
              <div className="flex flex-col lg:flex-row">
                {/* Chart */}
                <div className="lg:w-2/3 p-4 sm:p-6 border-b lg:border-b-0 lg:border-r border-slate-100" style={{ minHeight: '420px' }}>
                  <WakeCountyChart data={roofingData} selectedIndices={selectedIndices} />
                </div>
                {/* Simulation panel */}
                <div className="lg:w-1/3 p-6 bg-slate-50/40 flex flex-col gap-4">
                  <div>
                    <h4 className="text-base font-bold text-slate-800 mb-1">Try It Yourself</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      Most homeowners get 2–3 random bids. See what the odds look like when you don&apos;t have an advocate.
                    </p>
                  </div>
                  <button
                    onClick={handleSimulate}
                    disabled={isAnimating}
                    className={`w-full py-3 px-5 rounded-xl font-bold text-white text-sm transition-all duration-200 whitespace-nowrap
                      ${isAnimating ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 cursor-pointer'}`}
                  >
                    {isAnimating ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                        Simulating...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-shuffle-line" />
                        Simulate Random Bids
                      </span>
                    )}
                  </button>
                  {selectedIndices.length > 0 && (
                    <div className="rounded-xl overflow-hidden border border-slate-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="py-2 px-3 text-left font-semibold text-slate-600">Co #</th>
                            <th className="py-2 px-3 text-right font-semibold text-slate-600">Quality</th>
                            <th className="py-2 px-3 text-right font-semibold text-slate-600">Cost</th>
                            <th className="py-2 px-3 text-right font-semibold text-slate-600">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedIndices.map(i => {
                            const b = roofingData[i];
                            const sweet = b.value > 0.88 && b.quality > 8.6;
                            return (
                              <tr key={b.co} className="border-b border-slate-100 last:border-0">
                                <td className="py-2 px-3 font-bold text-orange-600">#{b.co}</td>
                                <td className="py-2 px-3 text-right text-slate-700">{b.quality.toFixed(1)}</td>
                                <td className="py-2 px-3 text-right text-slate-700">${b.cost.toLocaleString()}</td>
                                <td className={`py-2 px-3 text-right font-bold ${sweet ? 'text-emerald-700' : 'text-red-500'}`}>
                                  {b.value.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Verdict comment cards */}
                  {selectedIndices.length > 0 && (
                    <div className="space-y-2">
                      {selectedIndices.map(i => {
                        const b = roofingData[i];
                        const isSweet = b.value > 0.88 && b.quality > 8.6;
                        const isMid = b.value >= 0.74 && !isSweet;
                        return (
                          <div
                            key={`comment-${b.co}`}
                            className={`rounded-lg p-3 text-xs leading-relaxed border-l-4 ${
                              isSweet
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-800'
                                : isMid
                                ? 'bg-amber-50 border-amber-400 text-amber-900'
                                : 'bg-red-50 border-red-400 text-red-800'
                            }`}
                          >
                            <span className="font-bold">Co #{b.co}: </span>
                            {b.comment}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-auto pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-600 leading-relaxed">
                      <strong className="text-slate-800">With OverUnder Roofing</strong>, you&apos;re not gambling. We compare real bids and help you find fair value every time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── Bottom CTA ─── */}
        <section className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="rounded-2xl bg-emerald-700 p-8 sm:p-10 text-center text-white">
            <h3 className="text-xl sm:text-2xl font-extrabold mb-2">Stop Rolling the Dice on Your Roof</h3>
            <p className="text-emerald-100 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
              OverUnder Roofing gives you real bid comparisons, market data, and homeowner-first advocacy — so you always know you&apos;re getting fair value.
            </p>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-white text-emerald-700 font-bold px-7 py-3 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Get Your Free Bid Analysis
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <p className="text-center text-xs text-slate-400 mt-6 pb-4 max-w-2xl mx-auto">
            Data represents simulated Wake County market analysis for illustrative purposes. OverUnder Roofing is an independent advocate and is not a licensed contractor. NC General Statute §87-1 applies.
          </p>
        </section>
      </main>
    </div>
  );
}
